// InkChase — EnvelopeOps dashboard entry point.
// Auth gate pattern mirrors hubguard: useMe() → AuthenticatedApp →
// ConnectedShell (BrowserRouter + AppShell + Routes).
// Provider constant: 'docusign' (vs hubguard's 'hubspot').
import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import {
  BrowserRouter,
  Route,
  Routes,
  NavLink,
  Navigate,
} from 'react-router-dom';
import { QueryClientProvider, useQuery, useQueryClient } from '@tanstack/react-query';
import { LayoutDashboard, Users, Settings, LogOut } from 'lucide-react';
import { AppShell } from '@avagency/frontend/components/shell/AppShell';
import { Button } from '@avagency/frontend/components/ui/button';
import { Skeleton } from '@avagency/frontend/components/ui/skeleton';
import { Toaster } from '@avagency/frontend/components/ui/sonner';
import { createQueryClient } from '@avagency/frontend/lib/api';

import { Dashboard } from './pages/Dashboard';
import { Bottleneck } from './pages/Bottleneck';
import { RulesEditor } from './pages/RulesEditor';
import { SignIn } from './pages/SignIn';
import { ConnectDocuSign } from './pages/ConnectDocuSign';
import {
  AUTH_INVALIDATE_EVENT,
  clearOAuthCallbackStatusFromURL,
  fetchIntegrations,
  fetchMe,
  parseOAuthCallbackStatus,
  signOut,
  type IntegrationListItem,
  type IntegrationListResponse,
  type Me,
  type OAuthCallbackStatus,
} from './api';
import './index.css';

// Kit createQueryClient defaults: staleTime 30s, no refetchOnWindowFocus,
// retry up to 2 for non-4xx, no retry for 4xx.
const queryClient = createQueryClient();

// useMe — auth gate. /api/v1/auth/me returns the session (or 401).
// Strict freshness: staleTime 0, refetch on focus/reconnect, no retry on 401.
function useMe() {
  return useQuery<Me, Error & { status?: number }>({
    queryKey: ['auth', 'me'],
    queryFn: fetchMe,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
    retry: (failureCount, err) => {
      if (err.status === 401) return false;
      return failureCount < 1;
    },
  });
}

// DOCUSIGN_PROVIDER: literal the backend's IntegrationLister pins on.
// Defense-in-depth: even if the backend leaked another provider row,
// the dashboard would never reach for a non-DocuSign integration_id.
const DOCUSIGN_PROVIDER = 'docusign';

function useIntegrations(tenantID: string) {
  return useQuery<IntegrationListResponse, Error & { status?: number }>({
    queryKey: ['envelope-ops', 'integrations', tenantID],
    queryFn: fetchIntegrations,
  });
}

function pickPrimaryActive(list: IntegrationListItem[] | undefined): IntegrationListItem | undefined {
  if (!list) return undefined;
  return list.find((i) => i.provider === DOCUSIGN_PROVIDER && i.status === 'active');
}

function pickReauthRequired(list: IntegrationListItem[] | undefined): IntegrationListItem | undefined {
  if (!list) return undefined;
  return list.find((i) => i.provider === DOCUSIGN_PROVIDER && i.status === 'reauth_required');
}

// App — routes between SignIn and AuthenticatedApp.
// AUTH_INVALIDATE_EVENT (fired by envelopeFetch on any 401) invalidates
// the auth query so session loss kicks back to SignIn in one render cycle.
function App() {
  const me = useMe();
  const qc = useQueryClient();

  useEffect(() => {
    const handler = () => {
      qc.invalidateQueries({ queryKey: ['auth', 'me'] });
    };
    window.addEventListener(AUTH_INVALIDATE_EVENT, handler);
    return () => window.removeEventListener(AUTH_INVALIDATE_EVENT, handler);
  }, [qc]);

  if (me.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center p-8" aria-busy="true">
        <Skeleton className="h-32 w-full max-w-md" />
      </div>
    );
  }
  if (me.isError) {
    if (me.error.status === 401) {
      return <SignIn returnTo={window.location.pathname + window.location.search} />;
    }
    return <SignIn error={`Auth check failed: ${me.error.message}`} />;
  }
  return <AuthenticatedApp session={me.data!} />;
}

// AuthenticatedApp — checks DocuSign integration after /me succeeds.
//
// Branch precedence (top wins):
//   1. integrations loading → neutral skeleton
//   2. integrations error → ConnectDocuSign with error banner
//   3. has active DocuSign integration → ConnectedShell
//   4. no active, has reauth_required → ConnectDocuSign with reconnect banner
//   5. no integration at all → ConnectDocuSign onboarding
//
// Post-OAuth-callback status (av_oauth_status param) is surfaced via
// ConnectDocuSign's banner. URL cleanup runs once post-commit.
function AuthenticatedApp({ session }: { session: Me }) {
  const integrations = useIntegrations(session.tenant_id);

  const [callbackStatus] = React.useState<OAuthCallbackStatus>(parseOAuthCallbackStatus);
  React.useEffect(() => {
    clearOAuthCallbackStatusFromURL();
  }, []);

  if (integrations.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center p-8" aria-busy="true">
        <Skeleton className="h-32 w-full max-w-md" />
      </div>
    );
  }
  if (integrations.isError) {
    // A 401 from /integrations already fired AUTH_INVALIDATE_EVENT
    // via envelopeFetch; parent App will re-route to SignIn next render.
    return (
      <ConnectDocuSign
        email={session.email}
        onSignOut={() => signOut('/')}
        callbackStatus={{ kind: 'error', message: integrations.error.message }}
      />
    );
  }

  const active = pickPrimaryActive(integrations.data?.integrations);
  const reauthRequired = pickReauthRequired(integrations.data?.integrations);

  if (!active) {
    const banner: OAuthCallbackStatus = reauthRequired
      ? {
          kind: 'error',
          message:
            reauthRequired.last_error ||
            'Your DocuSign connection needs to be re-authorized.',
        }
      : callbackStatus;
    return (
      <ConnectDocuSign
        email={session.email}
        onSignOut={() => signOut('/')}
        callbackStatus={banner}
      />
    );
  }

  return <ConnectedShell session={session} integrationID={active.id} />;
}

// ConnectedShell — dashboard shell for tenants with an active DocuSign connection.
// AppShell provides the responsive layout; we own topbar + sidebar nav.
// 4 routes: / (envelopes + nudge), /bottleneck, /rules, /history (future).
function ConnectedShell({ session, integrationID }: { session: Me; integrationID: string }) {
  useEffect(() => {
    document.title = 'InkChase';
  }, []);

  return (
    <BrowserRouter>
      <AppShell
        productSlug="inkchase"
        topbar={<TopbarBrand email={session.email} />}
        sidebar={<Sidebar />}
      >
        <Routes>
          <Route path="/" element={<Dashboard integrationID={integrationID} />} />
          <Route path="/bottleneck" element={<Bottleneck />} />
          <Route path="/rules" element={<RulesEditor />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AppShell>
    </BrowserRouter>
  );
}

function TopbarBrand({ email }: { email: string }) {
  return (
    <div className="flex w-full items-center gap-3">
      <div
        className="eo-brand-orb flex-shrink-0"
        style={{ width: 24, height: 24 }}
        aria-hidden="true"
      />
      <span className="text-sm font-semibold tracking-tight">InkChase</span>
      <span className="hidden text-xs text-muted-foreground/70 sm:inline">
        DocuSign reminder &amp; signer-bottleneck dashboard
      </span>
      <div className="ml-auto flex items-center gap-3">
        <span className="hidden text-xs text-muted-foreground sm:inline">{email}</span>
        <Button variant="outline" size="sm" onClick={() => signOut('/')}>
          <LogOut className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
          Sign out
        </Button>
      </div>
    </div>
  );
}

function Sidebar() {
  const linkClass = (isActive: boolean) =>
    [
      'flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-150',
      isActive
        ? 'bg-primary/15 text-primary border border-primary/20 shadow-[0_0_12px_rgba(99,102,241,0.15)]'
        : 'text-foreground/70 hover:bg-white/5 hover:text-foreground border border-transparent',
    ].join(' ');

  return (
    <ul className="space-y-1">
      <li>
        <NavLink to="/" end className={({ isActive }) => linkClass(isActive)}>
          <LayoutDashboard className="h-4 w-4" />
          Envelopes
        </NavLink>
      </li>
      <li>
        <NavLink to="/bottleneck" className={({ isActive }) => linkClass(isActive)}>
          <Users className="h-4 w-4" />
          Bottleneck
        </NavLink>
      </li>
      <li>
        <NavLink to="/rules" className={({ isActive }) => linkClass(isActive)}>
          <Settings className="h-4 w-4" />
          Reminder Rules
        </NavLink>
      </li>
    </ul>
  );
}

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
      <Toaster />
    </QueryClientProvider>
  </React.StrictMode>,
);
